import { Component, OnInit } from '@angular/core';
import { ParamMap } from '@angular/router';
import { BackendService } from '../../services/backend.service';
import { WellRequest } from '../../model/well-request.model';
import { Observable, lastValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PermissionsService } from 'src/app/services/permissions.service';
import 'src/app/extensions/admin-array-extension'
import { IStatus } from '../../model/status.model';
import { WellReuqestBase } from '../well-base/well-request-base';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AbstractControl, FormBuilder, FormControl, Validators } from '@angular/forms';
import { WellRequestStatus_Options } from '../../model/status-enum.model';
import { RoutingService } from '../../services/routing.service';
import { EmailService } from '../../services/email.service';
import { IAdmin } from '../../model/admin.model';
import { MsalService } from '@azure/msal-angular';
import { IdTokenClaims } from '../../profile/profile.component';
import { WellRequestLog } from '../../model/well-request-log.model';
import { WellRequestAdmin } from '../../model/well-request-admin.model';

@Component({
    selector: 'app-well-details',
    templateUrl: './well-details.component.html',
    styleUrls: ['./well-details.component.css'],
    providers: [RoutingService],
    standalone: false
})
export class WellDetailsComponent extends WellReuqestBase implements OnInit {
  private get adminControl(): AbstractControl<any, any> { return this.form.controls['admin'] };

  wellRequest$!: Observable<WellRequest>;
  predefinedStatus: IStatus | undefined;
  wellRequestStatus: WellRequestStatus_Options | undefined;
  wellRequest: WellRequest | undefined;
  isCanEdit: boolean = false;
  isDisableSubmit: boolean = true;
  canAdminEdit: boolean = false;
  canRequestChanges: boolean = false;
  private canRequestChangesChecked: boolean = false;
  private pendingWellRequestId: string | null = null;

  filteredAdmins: IAdmin[] = [];
  selectedAdmins: IAdmin[] = [];
  initialAdmins: IAdmin[] = [];
  adminsForChangeRequest: IAdmin[] = [];

  constructor(
    _routingService: RoutingService,
    _snackBar: MatSnackBar,
    _fb: FormBuilder,
    readonly _msalService: MsalService,
    _backendService: BackendService,
    protected _permissionService: PermissionsService,
    _email: EmailService) {
    super(_routingService, _snackBar, _fb, _backendService, _email);
    this.form = this.fb.group({
      admin: ['', Validators.required],
      comments: ['', Validators.maxLength(250)]
    });
  }

  ngOnInit(): void {
    console.log('[WellDetailsComponent] ngOnInit - Initializing component');
    this.loadWells();
    this.checkCanAdminEdit();
    this.checkCanRequestChanges();
    this.loadAdminsForChangeRequest();
  }

  loadWells(): void {
    this.wellRequest$ = this.routingService.activatedRoute.paramMap.pipe(
      switchMap((params: ParamMap) =>
        this.backendService.getWellRequest(params.get('id') ?? ""))
    );

    this.wellRequest$.subscribe((data) => {
      this.wellRequest = data;
      if (this.wellRequest.id)
        this.backendService.isOwnerWellRequest(this.wellRequest.id.toString())
          .subscribe((isOwner) => {
            if (!isOwner)
              return;

            if (this.checkisEditable())
              this.setAdmins()
          })
    });
  }

  submitHandler(event: any) {
    let isSubmited = event as boolean;
    if (isSubmited)
      this.submitPopup();
  }

  canCancel(): boolean {
    return this._permissionService.HasWellRequestPermissions("WellRequest_CanCancel");
  }

  adminRemoveChanged(event: any) {
    let afterRemoveAdmins = event.afterRemove as IAdmin[];

    this.isDisableSubmit = afterRemoveAdmins.equalAdmins(this.initialAdmins)
                            || afterRemoveAdmins.length == 0;
  }

  adminSelectionChanged(event: any) {
    let admins = event as IAdmin[];

    this.isDisableSubmit = admins.equalAdmins(this.initialAdmins);
  }

  private submitPopup() {
    let claims: IdTokenClaims | undefined = this._msalService.instance.getActiveAccount()?.idTokenClaims;
    if (claims && claims.samaccountname) {
      if (!this.wellRequest?.groups
        || this.selectedAdmins.equalAdmins(this.initialAdmins)) 
        return;

      let adminIds = this.selectedAdmins
                                .getWellRequestAdmins(this.wellRequest?.groups)
                                .map(ag => ag.adminId) as string[];

      this.backendService.updateAdmins(adminIds,
        this.wellRequest.id?.toString() ?? "-1")
        .toPromise()
        .then(_ => this.handelLogRequest(
          (log: WellRequestLog) => this.backendService.logDuplicate(log),
          (log: WellRequestLog) => {
            this.sendEmailForNewAdmins(log,
              this.selectedAdmins.exceptAdmins(this.initialAdmins));
            this.initialAdmins = this.selectedAdmins.slice();
            this.isDisableSubmit = true;
          })
        );
    }
    else
      this._msalService.loginRedirect();
  }

  private sendEmailForNewAdmins(log: WellRequestLog | undefined, hallIds: string[]) {
    if (log?.wellRequestId && hallIds.length > 0)
      this.email.EmailNotify(BigInt(log?.wellRequestId), true, hallIds)
  }

  private checkisEditable(): boolean {
    if (this.wellRequest?.currentStatusId) {
      this.isCanEdit = [WellRequestStatus_Options.Created, WellRequestStatus_Options.Resubmited]
        .includes(this.wellRequest?.currentStatusId);

    }
    return this.isCanEdit;
  }

  private checkCanAdminEdit(): void {
    this.wellRequest$.subscribe(async (data) => {
      if (data?.currentStatusId) {
        const isValidStatus = [
          WellRequestStatus_Options.Created, // in progress
          WellRequestStatus_Options.Resubmited, // in progress
          WellRequestStatus_Options.Request_Changes,
          WellRequestStatus_Options.Approved,
          WellRequestStatus_Options.ApprovedActive,
          WellRequestStatus_Options.ApprovedInactive,
        ].includes(data.currentStatusId);

        /* invalid status:
        Cancelled_By_User = 11, 
        Cancelled_By_Admin = 12,
        */

        if (!isValidStatus) {
          this.canAdminEdit = false;
          return;
        }
        this.canAdminEdit = !!(await this.backendService.getCheckIsAdminsForAnyGroup().toPromise());
      }
    });
  }

  editRequest(): void {
    if (this.wellRequest?.id) {
      this.routingService.router.navigate(['/well-request/edit', this.wellRequest.id]);
    }
  }

  private async setAdmins(): Promise<void> {
    if (this.wellRequest?.groups) {
      const ids = this.wellRequest.groups.map(x => x.id).filter(x => x);
      console.log('[WellDetailsComponent] setAdmins - Loading admins for groups:', ids);
      
      try {
        this.filteredAdmins = await lastValueFrom(this.backendService.getAdminsByGroups(ids as string[]));
        console.log('[WellDetailsComponent] setAdmins - Loaded admins count:', this.filteredAdmins.length);
        this.adminControl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });

        this.preselectedAdmins(this.wellRequest.admins);
      } catch (error) {
        console.error('[WellDetailsComponent] setAdmins - Error loading admins:', error);
      }
    } else {
      console.warn('[WellDetailsComponent] setAdmins - No groups available for well request');
    }
  }

  private preselectedAdmins(adminsId: WellRequestAdmin[] | undefined) {
    if (adminsId) {
      console.log('[WellDetailsComponent] preselectedAdmins - Preselecting admins, count:', adminsId.length);
      let adminSelected = this.filteredAdmins.getAdmins(adminsId);;
      console.log('[WellDetailsComponent] preselectedAdmins - Selected admins count:', adminSelected.length);
      this.adminControl?.patchValue(adminSelected, { emitEvent: false });
      this.selectedAdmins = adminSelected;
      this.initialAdmins = this.selectedAdmins.slice();
    } else {
      console.warn('[WellDetailsComponent] preselectedAdmins - No admin IDs provided');
    }
  }

  private checkCanRequestChanges(): void {
    this.wellRequest$.subscribe(async (data) => {
      console.log('[WellDetailsComponent] checkCanRequestChanges - Checking permissions, currentStatusId:', data?.currentStatusId);
      if (data?.currentStatusId) {
        const isApprovedStatus = [
          WellRequestStatus_Options.Approved,
          WellRequestStatus_Options.ApprovedActive,
          WellRequestStatus_Options.ApprovedInactive
        ].includes(data.currentStatusId);

        console.log('[WellDetailsComponent] checkCanRequestChanges - isApprovedStatus:', isApprovedStatus);
        if (!isApprovedStatus) {
          this.canRequestChanges = false;
          this.canRequestChangesChecked = true;
          console.log('[WellDetailsComponent] checkCanRequestChanges - canRequestChanges set to false (invalid status)');
          return;
        }

        try {
          const isAdmin = await this.backendService.getCheckIsAdminsForAnyGroup().toPromise();
          this.canRequestChanges = !isAdmin;
          this.canRequestChangesChecked = true;
          console.log('[WellDetailsComponent] checkCanRequestChanges - isAdmin:', isAdmin, 'canRequestChanges:', this.canRequestChanges);
          
          if (this.canRequestChanges && data?.id) {
            this.pendingWellRequestId = data.id.toString();
            console.log('[WellDetailsComponent] checkCanRequestChanges - Stored pendingWellRequestId:', this.pendingWellRequestId);
            
            if (this.adminsForChangeRequest && this.adminsForChangeRequest.length > 0) {
              console.log('[WellDetailsComponent] checkCanRequestChanges - adminsForChangeRequest already loaded, calling loadWellRequestAdminsForChangeRequest immediately');
              this.loadWellRequestAdminsForChangeRequest(this.pendingWellRequestId);
              this.pendingWellRequestId = null;
            } else {
              console.log('[WellDetailsComponent] checkCanRequestChanges - adminsForChangeRequest not ready, will load after fetch completes');
            }
          }
        } catch (error) {
          console.error('[WellDetailsComponent] checkCanRequestChanges - Error checking admin status:', error);
          this.canRequestChanges = false;
          this.canRequestChangesChecked = true;
        }
      }
    });
  }

  private async loadAdminsForChangeRequest(): Promise<void> {
    console.log('[WellDetailsComponent] loadAdminsForChangeRequest - Starting to load all admins');
    try {
      this.adminsForChangeRequest = await lastValueFrom(this.backendService.getAdmins());
      console.log('[WellDetailsComponent] loadAdminsForChangeRequest - Loaded admins count:', this.adminsForChangeRequest.length);
      
      if (this.pendingWellRequestId && this.canRequestChanges) {
        console.log('[WellDetailsComponent] loadAdminsForChangeRequest - Processing pending wellRequestId:', this.pendingWellRequestId);
        this.loadWellRequestAdminsForChangeRequest(this.pendingWellRequestId);
        this.pendingWellRequestId = null;
      } else {
        console.log('[WellDetailsComponent] loadAdminsForChangeRequest - No pending wellRequestId or canRequestChanges is false');
      }
    } catch (error) {
      console.error('[WellDetailsComponent] loadAdminsForChangeRequest - Error loading admins:', error);
      this.adminsForChangeRequest = [];
    }
  }

  private loadWellRequestAdminsForChangeRequest(wellRequestId: string): void {
    console.log('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - Loading admins for wellRequestId:', wellRequestId);
    console.log('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - adminsForChangeRequest available:', this.adminsForChangeRequest.length);
    
    if (!this.adminsForChangeRequest || this.adminsForChangeRequest.length === 0) {
      console.log('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - adminsForChangeRequest is empty or undefined');
    }
    
    this.backendService.GetWellRequestAdmins(wellRequestId).subscribe({
      next: (admins: any[]) => {
        console.log('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - Received well request admins:', admins);
        const adminIds = admins.map(a => a.id);
        console.log('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - Admin IDs to filter:', adminIds);
        
        this.selectedAdmins = this.adminsForChangeRequest.filter(admin => adminIds.includes(admin.id));
        console.log('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - Filtered selectedAdmins count:', this.selectedAdmins.length);
        
        this.initialAdmins = this.selectedAdmins.slice();
        this.filteredAdmins = this.adminsForChangeRequest;
        console.log('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - Set filteredAdmins count:', this.filteredAdmins.length);
      },
      error: (error) => {
        console.error('[WellDetailsComponent] loadWellRequestAdminsForChangeRequest - Error loading well request admins:', error);
      }
    });
  }

  onAdminSelectedForChangeRequest(admins: any): void {
    this.selectedAdmins = admins as IAdmin[];
  }

  onAdminRemovedForChangeRequest(event: any): void {
    this.selectedAdmins = event.afterRemove as IAdmin[];
  }

  isAdminRemovableForChangeRequest = (event: any) => {
    let admin = event as IAdmin;
    return !admin.isTookAction;
  }

  async requestChangesSubmit(): Promise<void> {
    if (!this.validate(this.form.controls["comments"]))
      return;

    this.isFormDisabled = true;

    let log: WellRequestLog = new WellRequestLog();
    log.comments = this.form.getRawValue().comments;
    log.wellRequestId = parseInt(this.routingService.activatedRoute.snapshot.paramMap.get('id') ?? "0");

    await this.backendService.requestChanges(log).toPromise();
      
    if (log?.wellRequestId)
      this.email.EmailNotifyAll(BigInt(log.wellRequestId), undefined);

    this.form.disable();

    let snackBarRef = this.snackBar.open("Your request for changes has been successfully submitted to the admins and is currently awaiting approval", 
    "Close", { 
      horizontalPosition: 'right',
      panelClass: ['clive-snackbar-success'] 
    });

    snackBarRef.afterDismissed().subscribe(() => {
      this.redirect();
    });
  }

  onCommentsEnter(event: Event){
    event.preventDefault();
  }
}
